import React, { useState } from 'react'

import { ArtboardAnnotations } from 'modules/annotations/containers'
import { Share as AnnotationsShare } from 'modules/annotations/types'
import { ArtboardRevisions } from 'modules/versioning/components'

import SidebarHeader from '../SidebarHeader'
import { useShareSidebarTab, createTabs } from '../ShareSidebarTabContext'

import { SidebarWrapper, Wrapper } from './DetailSidebar.styles'
import { ArtboardDetailInfoFragment } from '@sketch/gql-types'

import { useVersioning } from 'modules/versioning'

interface DetailSidebarProps {
  share: AnnotationsShare
  artboard: ArtboardDetailInfoFragment
  userCanInspect: boolean
  toggleSidebar: () => void
  isViewingLatestRevision: boolean
  inspectorUI?: React.ReactNode
}

const DetailSidebar: React.FC<DetailSidebarProps> = ({
  share,
  artboard,
  userCanInspect,
  toggleSidebar,
  inspectorUI,
  isViewingLatestRevision,
}) => {
  // Check which tabs the user has access
  const [segments] = useState(
    createTabs({
      shouldShowComment: true,
      shouldShowInspector: userCanInspect,
      shouldShowVersion: true,
    })
  )
  const [activeSegment, setActiveSegment] = useShareSidebarTab(segments)
  const { isViewingLatestVersion } = useVersioning()

  // Render the selected tab
  let tab = null
  switch (activeSegment) {
    case 'Comment': {
      tab = (
        <ArtboardAnnotations
          share={share}
          permanentArtboardIdentifier={artboard.uuid}
          permanentArtboardShortId={artboard.permanentArtboardShortId || ''}
          permanentPageIdentifier={artboard.page?.uuid || ''}
          artboardNotificationStatus={artboard.subscriptionStatus}
          isViewingLatestVersion={
            isViewingLatestRevision && isViewingLatestVersion
          }
        />
      )
      break
    }
    case 'Version': {
      tab = (
        <ArtboardRevisions
          shareIdentifier={share.identifier}
          permanentArtboardIdentifier={artboard.uuid}
          permanentArtboardShortId={artboard.permanentArtboardShortId || ''}
          artboardRevisionIdentifier={artboard.revisionIdentifier || ''}
        />
      )
      break
    }
    case 'Inspect': {
      tab = inspectorUI
      break
    }
  }

  return (
    <Wrapper>
      <SidebarHeader
        segments={segments}
        currentValue={activeSegment}
        setValue={setActiveSegment}
        toggleSidebar={toggleSidebar}
      />
      {tab && <SidebarWrapper>{tab}</SidebarWrapper>}
    </Wrapper>
  )
}

/**
 * Renders an empty placeholder for the sidebar.
 */
export function DetailSidebarPlaceholder() {
  return <Wrapper />
}

export default DetailSidebar
